import React, { useState } from 'react';

import MarkdownRenderer from '../MarkdownRenderer';

interface MarkdownFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
  // Cmd/Ctrl+Enter shortcut — used by comment composers to submit without leaving the textarea
  // (plain Enter has to stay a newline once the field is multi-line).
  onSubmitShortcut?: () => void;
}

// Shared write/preview toggle for Markdown-capable text fields (task descriptions, comments).
// Read-only rendering elsewhere in the app already goes through MarkdownRenderer (chat bubbles) —
// this reuses it for the "Preview" tab so both surfaces stay visually consistent.
export const MarkdownField: React.FC<MarkdownFieldProps> = ({
  value,
  onChange,
  placeholder,
  rows = 5,
  className = 'textarea textarea-bordered w-full',
  disabled,
  onSubmitShortcut,
}) => {
  const [mode, setMode] = useState<'write' | 'preview'>('write');

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1 self-start rounded-lg bg-base-200 p-0.5">
        <button
          type="button"
          className={`btn btn-xs ${mode === 'write' ? 'btn-active' : 'btn-ghost'}`}
          onClick={() => setMode('write')}
        >
          Write
        </button>
        <button
          type="button"
          className={`btn btn-xs ${mode === 'preview' ? 'btn-active' : 'btn-ghost'}`}
          onClick={() => setMode('preview')}
        >
          Preview
        </button>
      </div>

      {mode === 'write' ? (
        <textarea
          className={className}
          placeholder={placeholder}
          rows={rows}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (onSubmitShortcut && (e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              onSubmitShortcut();
            }
          }}
        />
      ) : (
        <div className="rounded-lg bg-base-200 px-3 py-2 text-sm overflow-y-auto" style={{ minHeight: rows * 24 }}>
          {value.trim() ? (
            <MarkdownRenderer markdown={value} />
          ) : (
            <span className="opacity-40">Nothing to preview</span>
          )}
        </div>
      )}
    </div>
  );
};
